import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { buildSubdomain } from '@/lib/domain';
import {
  loadMyLinksStats,
  myLinksKindLabel,
  type MyLinksStats,
} from '@/lib/mylinksStats';

// ─────────────────────────────────────────────────────────────
// /dashboard/mylinks-stats — Statistiques du lien en bio (MyLinks).
// Vues, sources de trafic, offres les plus cliquées et série des
// 14 derniers jours. Données issues du capteur de la page publique.
// ─────────────────────────────────────────────────────────────

const SOURCE_META: Record<string, { label: string; icon: string; className: string }> = {
  direct: { label: 'Lien direct', icon: 'ri-link', className: 'bg-primary-100 text-primary-700' },
  facebook: { label: 'Facebook', icon: 'ri-facebook-circle-fill', className: 'bg-secondary-100 text-secondary-800' },
  instagram: { label: 'Instagram', icon: 'ri-instagram-line', className: 'bg-accent-100 text-accent-800' },
  tiktok: { label: 'TikTok', icon: 'ri-tiktok-fill', className: 'bg-foreground-100 text-foreground-800' },
  whatsapp: { label: 'WhatsApp', icon: 'ri-whatsapp-line', className: 'bg-accent-100 text-accent-800' },
  google: { label: 'Google', icon: 'ri-google-fill', className: 'bg-primary-100 text-primary-700' },
  x: { label: 'X', icon: 'ri-twitter-x-fill', className: 'bg-foreground-100 text-foreground-800' },
  linkedin: { label: 'LinkedIn', icon: 'ri-linkedin-box-fill', className: 'bg-secondary-100 text-secondary-800' },
  autre: { label: 'Autre site', icon: 'ri-global-line', className: 'bg-background-200/70 text-foreground-700' },
};

function sourceMeta(key: string) {
  return SOURCE_META[key] || { label: key, icon: 'ri-share-line', className: 'bg-background-100 text-foreground-700' };
}

export default function MyLinksStatsPage() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);
  const [stats, setStats] = useState<MyLinksStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setStats(await loadMyLinksStats(merchantId, days));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les statistiques.');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [merchantId, days]);

  useEffect(() => { void load(); }, [load]);

  const publicUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${import.meta.env.BASE_URL || '/'}mylinks`;
  }, []);

  const maxDaily = useMemo(
    () => Math.max(1, ...(stats?.daily.map((d) => d.count) || [1])),
    [stats],
  );

  const maxSource = stats?.sources[0]?.count || 1;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-link-m text-primary-500"></i>
            Statistiques MyLinks
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Trafic de votre lien en bio, sources d&apos;arrivée et offres les plus cliquées.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/mylinks"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-50 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors no-underline whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            Ouvrir ma page
          </Link>
          <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  days === d ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {d} j
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-24 rounded-lg border border-background-200/70 bg-background-50">
          <i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      ) : !stats ? (
        <div className="py-24 text-center rounded-lg border border-background-200/70 bg-background-50">
          <p className="text-sm text-foreground-500">Aucun commerce associé à ce compte.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line"></i>
                </span>
                <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Vues ({days} j)</p>
              </div>
              <p className="text-2xl font-bold font-heading text-foreground-950">{stats.totalViews}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{stats.todayViews} aujourd&apos;hui</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-group-line"></i>
                </span>
                <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Visiteurs uniques</p>
              </div>
              <p className="text-2xl font-bold font-heading text-foreground-950">{stats.uniqueVisitors}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{stats.weekViews} cette semaine</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-cursor-line"></i>
                </span>
                <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Clics sur les offres</p>
              </div>
              <p className="text-2xl font-bold font-heading text-foreground-950">{stats.totalClicks}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">Produits, services, annonces</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-percent-line"></i>
                </span>
                <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Taux de clic</p>
              </div>
              <p className="text-2xl font-bold font-heading text-primary-700">{stats.conversion} %</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">Clics / vues</p>
            </div>
          </div>

          {/* Série journalière */}
          <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2">
                <i className="ri-bar-chart-line text-primary-500"></i>
                Vues des 14 derniers jours
              </h3>
              <span className="text-[11px] text-foreground-400">max {maxDaily} / jour</span>
            </div>
            <div className="flex items-end gap-1.5 h-36">
              {stats.daily.map((d) => (
                <div key={d.label} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1 h-full">
                  <span className="text-[10px] text-foreground-500 tabular-nums">{d.count > 0 ? d.count : ''}</span>
                  <div
                    className={`w-full rounded-t-md transition-colors ${d.count > 0 ? 'bg-primary-500' : 'bg-background-200/60'}`}
                    style={{ height: `${Math.max(3, Math.round((d.count / maxDaily) * 100))}%` }}
                    title={`${d.count} vue(s) le ${d.label}`}
                  ></div>
                  <span className="text-[9px] text-foreground-400 whitespace-nowrap">{d.label.slice(0, 2)}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Sources */}
            <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
              <h3 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2 mb-3">
                <i className="ri-share-circle-line text-accent-500"></i>
                Sources de trafic
              </h3>
              {stats.sources.length === 0 ? (
                <div className="py-10 text-center">
                  <i className="ri-radar-line text-2xl text-foreground-300 block mb-2"></i>
                  <p className="text-xs text-foreground-500">Aucune visite enregistrée sur la période.</p>
                  <p className="text-[11px] text-foreground-400 mt-1">Partagez votre lien MyLinks pour voir arriver le trafic.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {stats.sources.map((s) => {
                    const meta = sourceMeta(s.label);
                    return (
                      <div key={s.label} className="flex items-center gap-2.5">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.className}`}>
                          <i className={`${meta.icon} text-xs`}></i>
                        </span>
                        <span className="w-20 text-xs text-foreground-700 truncate">{meta.label}</span>
                        <span className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                          <span
                            className="block h-full rounded-full bg-accent-500"
                            style={{ width: `${Math.max(4, Math.round((s.count / maxSource) * 100))}%` }}
                          ></span>
                        </span>
                        <span className="w-10 text-right text-xs font-semibold text-foreground-800 tabular-nums">{s.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Offres les plus cliquées */}
            <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="px-4 md:px-5 py-3 border-b border-background-200/70">
                <h3 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  Offres les plus cliquées
                </h3>
              </div>
              {stats.topOffers.length === 0 ? (
                <div className="py-10 text-center px-4">
                  <i className="ri-cursor-line text-2xl text-foreground-300 block mb-2"></i>
                  <p className="text-xs text-foreground-500">Aucun clic enregistré pour l&apos;instant.</p>
                </div>
              ) : (
                <div className="divide-y divide-background-200/70">
                  {stats.topOffers.map((o, i) => (
                    <div key={`${o.kind}-${o.id}`} className="flex items-center gap-3 px-4 md:px-5 py-2.5">
                      <span className="w-6 h-6 rounded-full bg-background-100 text-foreground-600 text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">{o.title}</p>
                        <p className="text-[11px] text-foreground-400">{myLinksKindLabel(o.kind)}{o.id > 0 ? ` #${o.id}` : ''}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold whitespace-nowrap">
                        {o.count} clic{o.count > 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Rappel du lien + accès à la personnalisation */}
          <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground-700">Votre lien à partager</p>
                <p className="text-xs text-foreground-500 mt-0.5 break-all">
                  {publicUrl || buildSubdomain(user?.user_name || '')}
                </p>
              </div>
              <Link
                to="/dashboard/store"
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors no-underline whitespace-nowrap self-start"
              >
                <i className="ri-palette-line"></i>
                Personnaliser MyLinks
              </Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}